import { useRef, useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { API_BASE } from '../config/api'
import SectionHeader from '../shared/components/ui/SectionHeader'
import ProjectCard from './cards/ProjectCard'

interface Project {
  id: string
  title: string
  description: string
  image?: string
  gallery?: string[]
  category?: string
  technologies?: string[]
  features?: string[]
  duration?: string
  team?: string
  status?: string
  rating?: number
  type?: 'business' | 'personal'
}

export default function Projects() {
  const ref = useRef(null)
  const [filter, setFilter] = useState('all')
  const [projects, setProjects] = useState<Project[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [categories, setCategories] = useState<string[]>([])
  const [isLoadingCategories, setIsLoadingCategories] = useState(false)


  const normalizeProject = (raw: unknown, index: number): Project => {
    const obj = (raw ?? {}) as Record<string, unknown>

    const technologies = Array.isArray(obj.technologies)
      ? obj.technologies.filter((t): t is string => typeof t === 'string')
      : []
    const features = Array.isArray(obj.features)
      ? obj.features.filter((f): f is string => typeof f === 'string')
      : []
    const gallery = Array.isArray(obj.gallery)
      ? obj.gallery.filter((g): g is string => typeof g === 'string')
      : undefined

    const id = typeof obj.id === 'string' && obj.id ? obj.id : `project-${index}`
    const type = obj.type === 'business' || obj.type === 'personal' ? obj.type : undefined

    return {
      id,
      title: typeof obj.title === 'string' && obj.title ? obj.title : 'Untitled Project',
      description: typeof obj.description === 'string' ? obj.description : '',
      image: typeof obj.image === 'string' && obj.image ? obj.image : '📱',
      gallery,
      category: typeof obj.category === 'string' ? obj.category : undefined,
      technologies,
      features,
      duration: typeof obj.duration === 'string' ? obj.duration : undefined,
      team: typeof obj.team === 'string' ? obj.team : undefined,
      status: typeof obj.status === 'string' ? obj.status : undefined,
      rating: typeof obj.rating === 'number' ? obj.rating : undefined,
      type,
    }
  }

  // Fetch projects based on selected category
  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const endpoint = filter === 'all'
          ? `${API_BASE}/api/v1/projects`
          : `${API_BASE}/api/v1/projects?category=${encodeURIComponent(filter)}`

        const response = await fetch(endpoint)
        if (!response.ok) {
          throw new Error(`Projects API request failed: ${response.status} ${response.statusText}`)
        }
        const data = await response.json()

        const list: unknown[] = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
        const normalized = list.map((p: unknown, i: number) => normalizeProject(p, i))
        setProjects(normalized)

        if (!Array.isArray(list) || (data?.success === false && data?.error)) {
          setError(typeof data?.error === 'string' ? data.error : 'Unexpected API response')
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error)
        setProjects([])
        setError(error instanceof Error ? error.message : 'Failed to fetch projects')
      } finally {
        setIsLoading(false)
      }
    }
    fetchProjects()
  }, [filter])

  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoadingCategories(true)
      try {
        const response = await fetch(`${API_BASE}/api/v1/projects/categories`)
        if (!response.ok) {
          throw new Error(`Projects categories request failed: ${response.status} ${response.statusText}`)
        }
        const data = await response.json()
        const list: unknown[] = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
        const normalized = Array.from(
          new Set(list.filter((item): item is string => typeof item === 'string' && item.trim().length > 0))
        ).map((item) => item.trim().toLowerCase())
        setCategories(normalized)
      } catch (err) {
        console.error('Failed to fetch project categories:', err)
      } finally {
        setIsLoadingCategories(false)
      }
    }

    fetchCategories()
  }, [])

  useEffect(() => {
    if (!categories.length) return
    const available = new Set(categories)
    if (filter !== 'all' && !available.has(filter)) {
      setFilter('all')
    }
  }, [categories, filter])

  const formatCategoryLabel = (slug: string) => {
    const map: Record<string, string> = {
      mobile: 'Mobile Apps',
      extension: 'Extensions',
      automation: 'Automation',
      ecommerce: 'E-Commerce',
      'open-source': 'Open Source',
      web: 'Web Apps',
      general: 'General',
    }
    if (map[slug]) return map[slug]
    return slug
      .split('-')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  }

  const filters = useMemo(() => {
    if (!categories.length) {
      return [
        { id: 'all', label: 'All Projects' },
        { id: 'mobile', label: 'Mobile Apps' },
        { id: 'extension', label: 'Extensions' },
        { id: 'automation', label: 'Automation' },
      ]
    }

    return [
      { id: 'all', label: 'All Projects' },
      ...categories.map((slug) => ({ id: slug, label: formatCategoryLabel(slug) })),
    ]
  }, [categories])

  return (
    <section id="projects" ref={ref} className="section-spacing" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <div className="container-page">
        <SectionHeader
          badge="Our Work"
          title="Featured Projects"
          description="Explore our portfolio of innovative digital solutions that have helped businesses transform their operations and engage with customers in new ways."
        />

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {filters.map((filterItem) => {
            const isActive = filter === filterItem.id
            return (
              <button
                key={filterItem.id}
                onClick={() => setFilter(filterItem.id)}
                className="btn-pill"
                style={{
                  background: isActive ? 'var(--btn-bg)' : 'var(--surface-2)',
                  color: isActive ? 'var(--btn-text)' : 'var(--muted)',
                  border: isActive ? '1px solid var(--btn-bg)' : '1px solid var(--border)',
                  cursor: 'pointer',
                  padding: '10px 20px',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                }}
              >
                {filterItem.label}
              </button>
            )
          })}
        </div>

        {isLoadingCategories && (
          <div className="text-center text-xs mb-8" style={{ color: 'var(--muted)' }}>
            Syncing categories...
          </div>
        )}

        {/* Loading / Error States */}
        {error && !isLoading && (
          <div className="text-center py-8">
            <p style={{ color: 'var(--muted)' }}>{error}</p>
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="shimmer-card" style={{ height: '480px' }}>
                <div>
                  {/* Header skeleton */}
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-lg shimmer-item flex-shrink-0" />
                    <div className="flex-1 space-y-2 mt-1">
                      <div className="h-4 w-3/4 rounded shimmer-item" />
                      <div className="h-3 w-1/3 rounded shimmer-item" />
                    </div>
                  </div>
                  {/* Description skeleton */}
                  <div className="space-y-2 mb-6">
                    <div className="h-3 w-full rounded shimmer-item" />
                    <div className="h-3 w-full rounded shimmer-item" />
                    <div className="h-3 w-2/3 rounded shimmer-item" />
                  </div>
                  {/* Tags skeleton */}
                  <div className="flex gap-2 mb-6">
                    <div className="h-6 w-16 rounded-full shimmer-item" />
                    <div className="h-6 w-20 rounded-full shimmer-item" />
                  </div>
                </div>
                {/* Footer skeleton */}
                <div className="flex justify-between items-center pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                  <div className="h-3 w-24 rounded shimmer-item" />
                  <div className="h-3 w-16 rounded shimmer-item" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Projects Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.length === 0 ? (
              <div className="col-span-full text-center py-16">
                <p style={{ color: 'var(--muted)', fontSize: '1.1rem' }}>
                  No projects found in this category.
                </p>
              </div>
            ) : (
              projects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))
            )}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-20 text-center">
          <div className="cta-block" style={{ maxWidth: '700px', margin: '0 auto' }}>
            <h3 className="font-display text-2xl font-semibold" style={{ color: 'var(--inverse-text)' }}>
              Ready to Start Your Project?
            </h3>
            <p className="mt-3 text-[1.06rem]" style={{ color: 'var(--inverse-text)', opacity: 0.6, maxWidth: '500px', margin: '12px auto 0' }}>
              Let&apos;s discuss how we can bring your vision to life with our expertise and innovative approach to development.
            </p>
            <div className="mt-8">
              <Link to="/start-project" className="btn-pill btn-inverse no-underline">
                Start Your Project
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
